import {
  accountMutationHeaders,
  readBoundedAccountJson,
} from "./account-state-session-repository.js";
import {
  createAccountOperationId,
  isAccountOperationId,
} from "./account-operation-id.js";

const SCHEMA_VERSION = 1;
const RESPONSE_MAX_BYTES = 16 * 1024;
const ACCOUNT_PATH = "/api/v1/account/appearance-preferences";
const THEME_NAMES = Object.freeze([
  "fishy",
  "dark",
  "abyss",
  "light",
  "silk",
  "cupcake",
  "caramellatte",
]);
const DOMAIN_COLOR_MODES = Object.freeze([
  "game",
  "theme-with-game-grades",
  "theme",
]);
const COMMAND_FIELDS = Object.freeze({
  createTheme: ["kind", "schemaVersion", "operationId", "selection"],
  setTheme: [
    "kind",
    "schemaVersion",
    "operationId",
    "expectedRevision",
    "selection",
  ],
  resetTheme: ["kind", "schemaVersion", "operationId", "expectedRevision"],
  createDomainColors: ["kind", "schemaVersion", "operationId", "mode"],
  setDomainColors: [
    "kind",
    "schemaVersion",
    "operationId",
    "expectedRevision",
    "mode",
  ],
  resetDomainColors: [
    "kind",
    "schemaVersion",
    "operationId",
    "expectedRevision",
  ],
});

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactFields(value, expectedFields) {
  if (!isRecord(value)) return false;
  const actualFields = Object.keys(value).sort();
  const sortedExpected = expectedFields.slice().sort();
  return actualFields.length === sortedExpected.length
    && actualFields.every((field, index) => field === sortedExpected[index]);
}

function positiveSafeInteger(value) {
  return Number.isSafeInteger(value) && value > 0;
}

function freezeSelection(selection) {
  return selection.kind === "system"
    ? Object.freeze({ kind: "system" })
    : Object.freeze({ kind: "named", name: selection.name });
}

function freezeThemeProjection(value) {
  return value.kind === "missing"
    ? Object.freeze({ kind: "missing" })
    : Object.freeze({
      kind: "ready",
      revision: value.revision,
      selection: freezeSelection(value.selection),
    });
}

function freezeDomainColorProjection(value) {
  return value.kind === "missing"
    ? Object.freeze({ kind: "missing" })
    : Object.freeze({
      kind: "ready",
      revision: value.revision,
      mode: value.mode,
    });
}

function freezeProjection(value) {
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    theme: freezeThemeProjection(value.theme),
    domainColors: freezeDomainColorProjection(value.domainColors),
  });
}

function freezeState(value) {
  const next = { ...value };
  if (next.authoritativeState) {
    next.authoritativeState = freezeProjection(next.authoritativeState);
  }
  return Object.freeze(next);
}

function decodeThemeSelection(value) {
  if (hasExactFields(value, ["kind"]) && value.kind === "system") {
    return freezeSelection(value);
  }
  if (
    !hasExactFields(value, ["kind", "name"])
    || value.kind !== "named"
    || !THEME_NAMES.includes(value.name)
  ) {
    throw new TypeError("Account appearance theme selection is invalid.");
  }
  return freezeSelection(value);
}

function decodeThemeProjection(value) {
  if (hasExactFields(value, ["kind"]) && value.kind === "missing") {
    return freezeThemeProjection(value);
  }
  if (
    !hasExactFields(value, ["kind", "revision", "selection"])
    || value.kind !== "ready"
    || !positiveSafeInteger(value.revision)
  ) {
    throw new TypeError("Account appearance theme projection is invalid.");
  }
  return freezeThemeProjection({
    kind: "ready",
    revision: value.revision,
    selection: decodeThemeSelection(value.selection),
  });
}

function decodeDomainColorProjection(value) {
  if (hasExactFields(value, ["kind"]) && value.kind === "missing") {
    return freezeDomainColorProjection(value);
  }
  if (
    !hasExactFields(value, ["kind", "revision", "mode"])
    || value.kind !== "ready"
    || !positiveSafeInteger(value.revision)
    || !DOMAIN_COLOR_MODES.includes(value.mode)
  ) {
    throw new TypeError("Account appearance domain-color projection is invalid.");
  }
  return freezeDomainColorProjection(value);
}

export function decodeAppearancePreferencesProjectionV1(value) {
  if (
    !hasExactFields(value, ["schemaVersion", "theme", "domainColors"])
    || value.schemaVersion !== SCHEMA_VERSION
  ) {
    throw new TypeError("Account appearance preferences projection is invalid.");
  }
  return freezeProjection({
    theme: decodeThemeProjection(value.theme),
    domainColors: decodeDomainColorProjection(value.domainColors),
  });
}

export function decodeAppearancePreferencesCommandV1(value) {
  const expectedFields = COMMAND_FIELDS[value?.kind];
  if (
    !expectedFields
    || !hasExactFields(value, expectedFields)
    || value.schemaVersion !== SCHEMA_VERSION
    || typeof value.operationId !== "string"
    || !isAccountOperationId(value.operationId)
  ) {
    throw new TypeError("Account appearance preferences command is invalid.");
  }
  if (value.kind.includes("Theme") && !value.kind.startsWith("reset")) {
    decodeThemeSelection(value.selection);
  }
  if (value.kind.includes("DomainColors") && !value.kind.startsWith("reset")) {
    if (!DOMAIN_COLOR_MODES.includes(value.mode)) {
      throw new TypeError("Account appearance preferences command is invalid.");
    }
  }
  if (
    (value.kind.startsWith("set") || value.kind.startsWith("reset"))
    && !positiveSafeInteger(value.expectedRevision)
  ) {
    throw new TypeError("Account appearance preferences command is invalid.");
  }
  return Object.freeze({
    ...value,
    ...(value.selection ? { selection: decodeThemeSelection(value.selection) } : {}),
  });
}

export function decodeAppearancePreferencesAcknowledgementV1(value, operationId) {
  if (
    !hasExactFields(value, ["kind", "operationId", "authoritativeState"])
    || !["applied", "conflict"].includes(value.kind)
    || value.operationId !== operationId
  ) {
    throw new TypeError("Account appearance preferences acknowledgement is invalid.");
  }
  return Object.freeze({
    kind: value.kind,
    operationId,
    authoritativeState:
      decodeAppearancePreferencesProjectionV1(value.authoritativeState),
  });
}

function accountUrl(apiBaseUrl) {
  let base;
  try {
    base = new URL(String(apiBaseUrl ?? ""));
  } catch (_error) {
    throw new TypeError("Appearance preferences API base URL is invalid.");
  }
  if (
    !["http:", "https:"].includes(base.protocol)
    || base.username
    || base.password
    || base.pathname !== "/"
    || base.search
    || base.hash
  ) {
    throw new TypeError("Appearance preferences API base URL must be an HTTP origin.");
  }
  return `${base.origin}${ACCOUNT_PATH}`;
}

export function createAppearancePreferencesAccountProvider({
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
} = {}) {
  if (typeof fetchImpl !== "function") {
    throw new TypeError("Appearance preferences provider requires fetch.");
  }
  const url = accountUrl(apiBaseUrl);

  async function load({ signal } = {}) {
    const response = await fetchImpl(url, {
      method: "GET",
      cache: "no-store",
      credentials: "include",
      headers: { Accept: "application/json" },
      redirect: "error",
      signal,
    });
    if (response.status === 401) return freezeState({ kind: "signedOut" });
    if (!response.ok) throw new Error("Appearance preferences request failed.");
    return decodeAppearancePreferencesProjectionV1(
      await readBoundedAccountJson(
        response,
        "Account appearance preferences",
        RESPONSE_MAX_BYTES,
      ),
    );
  }

  async function mutate(session, command, { signal } = {}) {
    const exactCommand = decodeAppearancePreferencesCommandV1(command);
    const response = await fetchImpl(url, {
      method: "POST",
      cache: "no-store",
      credentials: "include",
      headers: accountMutationHeaders(session),
      redirect: "error",
      signal,
      body: JSON.stringify(exactCommand),
    });
    if (response.status === 401) return freezeState({ kind: "signedOut" });
    if (![200, 409].includes(response.status)) {
      throw new Error("Appearance preferences mutation failed.");
    }
    const acknowledgement = decodeAppearancePreferencesAcknowledgementV1(
      await readBoundedAccountJson(
        response,
        "Account appearance preferences acknowledgement",
        RESPONSE_MAX_BYTES,
      ),
      exactCommand.operationId,
    );
    if (
      (response.status === 200 && acknowledgement.kind !== "applied")
      || (response.status === 409 && acknowledgement.kind !== "conflict")
    ) {
      throw new TypeError(
        "Appearance preferences status and acknowledgement disagree.",
      );
    }
    return acknowledgement;
  }

  return Object.freeze({ load, mutate });
}

function hasMethods(value, names) {
  return names.every((name) => typeof value?.[name] === "function");
}

function expectedRevision(options, label) {
  if (options === undefined) return undefined;
  if (
    !isRecord(options)
    || Object.keys(options).some((field) => field !== "expectedRevision")
  ) {
    throw new TypeError(`${label} options contain unsupported fields.`);
  }
  if (options.expectedRevision === undefined || options.expectedRevision === null) {
    return options.expectedRevision;
  }
  if (!positiveSafeInteger(options.expectedRevision)) {
    throw new TypeError(`${label} expected revision is invalid.`);
  }
  return options.expectedRevision;
}

export function createAuthenticatedAppearancePreferencesRepository({
  sessionRepository,
  accountProvider = null,
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
  createOperationId = createAccountOperationId,
} = {}) {
  if (!hasMethods(sessionRepository, ["load", "refresh", "state", "subscribe"])) {
    throw new TypeError("Appearance preferences require the account session repository.");
  }
  const remote = accountProvider ?? createAppearancePreferencesAccountProvider({
    apiBaseUrl,
    fetchImpl,
  });
  if (!hasMethods(remote, ["load", "mutate"]) || typeof createOperationId !== "function") {
    throw new TypeError("Appearance preferences require their typed account provider.");
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let authoritativeState = null;
  let accountId = null;
  let requestId = 0;
  let requestController = null;
  let sessionLoadDepth = 0;
  let disposed = false;
  const listeners = new Set();

  function state() {
    return currentState;
  }

  function deliver(listener) {
    try {
      listener(currentState);
    } catch (_error) {
      globalThis.reportError?.(new Error("Appearance preferences subscriber failed."));
    }
  }

  function publish(value) {
    if (disposed) return currentState;
    const nextState = freezeState(value);
    if (JSON.stringify(currentState) === JSON.stringify(nextState)) return currentState;
    currentState = nextState;
    for (const listener of listeners) deliver(listener);
    return currentState;
  }

  function beginRequest({ loading = false } = {}) {
    requestId += 1;
    requestController?.abort();
    requestController = new AbortController();
    if (loading) publish({ kind: "loading" });
    return Object.freeze({ id: requestId, controller: requestController });
  }

  function isCurrent(request) {
    return !disposed
      && request.id === requestId
      && !request.controller.signal.aborted;
  }

  function clearAuthority(value) {
    authoritativeState = null;
    accountId = null;
    return publish(value);
  }

  function applyProjection(projection, nextAccountId) {
    authoritativeState = projection;
    accountId = nextAccountId;
    return publish({ kind: "ready", authoritativeState: projection });
  }

  function unavailable(operation, reason) {
    return clearAuthority({ kind: "unavailable", operation, reason });
  }

  async function freshSession(request, { refresh = false } = {}) {
    sessionLoadDepth += 1;
    try {
      const session = await sessionRepository[refresh ? "refresh" : "load"]();
      return isCurrent(request) ? session : null;
    } finally {
      sessionLoadDepth -= 1;
    }
  }

  function applySessionBoundary(session) {
    if (session?.kind === "signedOut") {
      return clearAuthority({ kind: "signedOut" });
    }
    if (session?.kind === "locked") {
      return clearAuthority({
        kind: "locked",
        reason: session.reason || "account-locked",
      });
    }
    if (session?.kind === "unavailable") {
      return unavailable("account", session.reason || "session-request-failed");
    }
    if (session?.kind !== "ready") {
      return clearAuthority({ kind: "loading" });
    }
    return null;
  }

  async function readRemote(request, session) {
    try {
      const projection = await remote.load({ signal: request.controller.signal });
      if (!isCurrent(request)) return state();
      if (projection.kind === "signedOut") {
        const refreshed = await freshSession(request, { refresh: true });
        if (!refreshed || !isCurrent(request)) return state();
        if (applySessionBoundary(refreshed) || refreshed.kind !== "ready") return state();
        return unavailable("load", "account-authorization-rejected");
      }
      return applyProjection(projection, session.accountId);
    } catch (error) {
      if (!isCurrent(request) || error?.name === "AbortError") return state();
      return unavailable("load", "appearance-request-failed");
    }
  }

  async function load() {
    if (disposed) return state();
    const request = beginRequest({ loading: true });
    const session = await freshSession(request);
    if (!session || !isCurrent(request)) return state();
    if (applySessionBoundary(session) || session.kind !== "ready") return state();
    return readRemote(request, session);
  }

  function operationId() {
    const value = createOperationId();
    if (!isAccountOperationId(value)) {
      throw new TypeError("Appearance preferences operation ID is invalid.");
    }
    return value;
  }

  function commandFor(operation, value) {
    const field = operation.endsWith("Theme") ? "theme" : "domainColors";
    const projection = authoritativeState?.[field];
    if (!projection) return null;
    const isReset = operation.startsWith("reset");
    if (isReset && projection.kind === "missing") return null;
    const createKind = field === "theme" ? "createTheme" : "createDomainColors";
    const setKind = field === "theme" ? "setTheme" : "setDomainColors";
    return decodeAppearancePreferencesCommandV1({
      kind: isReset
        ? operation
        : projection.kind === "missing" ? createKind : setKind,
      schemaVersion: SCHEMA_VERSION,
      operationId: operationId(),
      ...(projection.kind === "ready"
        ? { expectedRevision: projection.revision }
        : {}),
      ...(field === "theme" && !isReset ? { selection: value } : {}),
      ...(field === "domainColors" && !isReset ? { mode: value } : {}),
    });
  }

  async function mutate(operation, value, options, label) {
    const expected = expectedRevision(options, label);
    if (currentState.kind === "pending") return state();
    if (!authoritativeState || !accountId) return state();
    const field = operation.endsWith("Theme") ? "theme" : "domainColors";
    const currentRevision = authoritativeState[field].kind === "ready"
      ? authoritativeState[field].revision
      : null;
    if (expected !== undefined && expected !== currentRevision) {
      return publish({
        kind: "conflict",
        operation,
        expectedRevision: expected,
        authoritativeState,
      });
    }
    const command = commandFor(operation, value);
    if (!command) return state();
    const startingAccountId = accountId;
    const request = beginRequest();
    publish({ kind: "pending", operation, authoritativeState });

    try {
      const session = await freshSession(request, { refresh: true });
      if (!session || !isCurrent(request)) return state();
      if (applySessionBoundary(session) || session.kind !== "ready") return state();
      if (session.accountId !== startingAccountId) {
        return unavailable(operation, "account-authority-changed");
      }
      const acknowledgement = await remote.mutate(session, command, {
        signal: request.controller.signal,
      });
      if (!isCurrent(request)) return state();
      if (acknowledgement.kind === "signedOut") {
        const refreshed = await freshSession(request, { refresh: true });
        if (!refreshed || !isCurrent(request)) return state();
        if (applySessionBoundary(refreshed) || refreshed.kind !== "ready") return state();
        return unavailable(operation, "account-authorization-rejected");
      }
      authoritativeState = acknowledgement.authoritativeState;
      accountId = session.accountId;
      if (acknowledgement.kind === "conflict") {
        return publish({
          kind: "conflict",
          operation,
          expectedRevision: currentRevision,
          authoritativeState,
        });
      }
      return publish({ kind: "ready", authoritativeState });
    } catch (error) {
      if (!isCurrent(request) || error?.name === "AbortError") return state();
      return unavailable(operation, "appearance-mutation-failed");
    }
  }

  function setTheme(selection, options) {
    return mutate("setTheme", decodeThemeSelection(selection), options, "Theme command");
  }

  function resetTheme(options) {
    return mutate("resetTheme", null, options, "Theme reset");
  }

  function setDomainColors(mode, options) {
    if (!DOMAIN_COLOR_MODES.includes(mode)) {
      throw new TypeError("Domain color mode is invalid.");
    }
    return mutate("setDomainColors", mode, options, "Domain color command");
  }

  function resetDomainColors(options) {
    return mutate("resetDomainColors", null, options, "Domain color reset");
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Appearance preferences listener must be a function.");
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  const unsubscribeSession = sessionRepository.subscribe((session) => {
    if (disposed || sessionLoadDepth > 0) return;
    if (session.kind !== "ready") {
      beginRequest();
      applySessionBoundary(session);
      return;
    }
    const request = beginRequest({ loading: true });
    void readRemote(request, session);
  });

  function dispose() {
    if (disposed) return;
    disposed = true;
    requestId += 1;
    requestController?.abort();
    requestController = null;
    listeners.clear();
    unsubscribeSession();
  }

  return Object.freeze({
    dispose,
    load,
    resetDomainColors,
    resetTheme,
    setDomainColors,
    setTheme,
    state,
    subscribe,
  });
}
